import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, Star, Quote } from "lucide-react";
import axios from "axios";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { fallbackServices } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://api.elysana-massage.de";
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState(fallbackServices);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/services`, {
        timeout: 5000,
      });
      
      // Ensure response.data is an array
      if (Array.isArray(response.data) && response.data.length > 0) {
        setServices(response.data);
      } else {
        // Fallback to static data if API returns invalid data
        setServices(fallbackServices);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      // Fallback to static data on error
      setServices(fallbackServices);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookNow = (serviceId) => {
    navigate(`/book/${serviceId}`);
  };

  const serviceImages = {
    "aroma-oil": "https://images.pexels.com/photos/7148532/pexels-photo-7148532.jpeg",
    "hot-stone": "https://images.unsplash.com/photo-1610402601271-5b4bd5b3eba4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxob3QlMjBzdG9uZSUyMG1hc3NhZ2UlMjB0aGVyYXB5fGVufDB8fHx8MTc2NDY4Mzk5N3ww&ixlib=rb-4.1.0&q=85",
    "abhyanga": "https://images.unsplash.com/photo-1649751295468-953038600bef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxheXVydmVkaWMlMjBtYXNzYWdlJTIwb2lsJTIwdHJlYXRtZW50fGVufDB8fHx8MTc2NDY4NDAxMnww&ixlib=rb-4.1.0&q=85",
    "full-body": "https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxzcGElMjBtYXNzYWdlJTIwdGhlcmFweXxlbnwwfHx8fDE3NjQ2ODM5OTB8MA&ixlib=rb-4.1.0&q=85",
    "thai": "https://images.unsplash.com/photo-1559185590-d545a0c5a1dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxmb290JTIwbWFzc2FnZSUyMHNwYSUyMHRoZXJhcHl8ZW58MHx8fHwxNzY0Njg0MDA0fDA&ixlib=rb-4.1.0&q=85",
    "sanjeevani": "https://images.pexels.com/photos/6724401/pexels-photo-6724401.jpeg",
    "leg-foot": "https://images.pexels.com/photos/5240642/pexels-photo-5240642.jpeg",
    "mukabhyanga": "https://images.unsplash.com/photo-1722350766824-f8520e9676ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxmYWNpYWwlMjBtYXNzYWdlJTIwc3BhJTIwcmVsYXhpbmd8ZW58MHx8fHwxNzY0Njg0NzI3fDA&ixlib=rb-4.1.0&q=85",
    "back": "https://images.pexels.com/photos/6560266/pexels-photo-6560266.jpeg"
  };

  return (
    <div className="min-h-screen grain-texture">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-4 z-50 mx-4 md:mx-8 mt-4"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 rounded-full border border-white/20 bg-white/70 backdrop-blur-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img 
                src="https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/up1s5d57_Screenshot_2025-12-10_at_01.58.41-removebg-preview.png"
                alt="Elysana Logo"
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-primary hover:text-accent transition-colors">Leistungen</a>
              <a href="/all-services" className="text-primary hover:text-accent transition-colors">Alle Leistungen</a>
              <a href="/faq" className="text-primary hover:text-accent transition-colors">FAQ</a>
              <a href="/contact" className="text-primary hover:text-accent transition-colors">Kontakt</a>
              <button
                onClick={() => navigate('/all-services')}
                className="bg-accent text-white px-6 py-3 rounded-full font-medium hover:bg-accent/90 transition-all shadow-md"
                data-testid="header-book-appointment-btn"
              >
                Termin buchen
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxzcGElMjBtYXNzYWdlJTIwcmVsYXhpbmclMjBhdG1vc3BoZXJlfGVufDB8fHx8MTc2NDY4MDI1NXww&ixlib=rb-4.1.0&q=85"
            alt="Entspannender Spa"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/20 to-background"></div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl mx-auto px-6 text-center"
        >
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            Erleben Sie wahre
            <span className="block text-accent mt-2">Wellness & Entspannung</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-2xl mx-auto">
            Professionelle Massagetherapie, die alte Traditionen mit modernen Techniken für Ihren ultimativen Komfort kombiniert
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
            data-testid="explore-services-btn"
          >
            Unsere Leistungen entdecken
          </motion.button>
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Unsere Leistungen</h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Wählen Sie aus unserer kuratierten Auswahl an professionellen Massage- und Wellnessbehandlungen
            </p>
          </motion.div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-text-secondary">Leistungen werden geladen...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.isArray(services) && services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="service-card group flex flex-col"
                  onClick={() => handleBookNow(service.id)}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="h-64 rounded-xl overflow-hidden mb-6">
                    <img 
                      src={serviceImages[service.id]}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-3">{service.title}</h3>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {service.originalPrice && (
                        <p className="text-lg text-text-muted line-through">{service.originalPrice}</p>
                      )}
                      <p className="text-2xl font-bold text-accent">{service.price}</p>
                    </div>
                    <p className="text-sm text-text-muted">{service.duration}</p>
                  </div>
                  <p className="text-text-secondary mb-6 flex-grow">{service.description}</p>
                  <button 
                    className="btn-primary w-full mt-auto"
                    data-testid={`book-btn-${service.id}`}
                  >
                    Jetzt buchen
                  </button>
                </motion.div>
              ))}
            </div>
          )}
          
          {/* View All Services Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <button
              onClick={() => navigate('/all-services')}
              className="btn-secondary"
              data-testid="view-all-services-btn"
            >
              Alle Leistungen anzeigen
            </button>
          </motion.div>
        </div>
      </section>

      {/* Best Selling Services Section */}
      <section className="py-24 px-6 bg-accent/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Meistgebuchte Behandlungen</h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Unsere beliebtesten Massageleistungen, die von Kunden geliebt werden
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Array.isArray(services) && services.slice(0, 3).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="service-card group relative overflow-hidden flex flex-col"
                onClick={() => handleBookNow(service.id)}
                data-testid={`bestseller-card-${service.id}`}
              >
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-accent text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    Meistgebucht
                  </div>
                </div>
                <div className="h-64 rounded-xl overflow-hidden mb-6">
                  <img 
                    src={serviceImages[service.id]}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-2xl font-bold text-primary mb-3">{service.title}</h3>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {service.originalPrice && (
                      <p className="text-lg text-text-muted line-through">{service.originalPrice}</p>
                    )}
                    <p className="text-2xl font-bold text-accent">{service.price}</p>
                  </div>
                  <p className="text-sm text-text-muted">{service.duration}</p>
                </div>
                <p className="text-text-secondary mb-6 flex-grow">{service.description}</p>
                <button 
                  className="btn-primary w-full mt-auto"
                  data-testid={`bestseller-book-btn-${service.id}`}
                >
                  Jetzt buchen
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-secondary/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Über Elysana</h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Bei Elysana Massage glauben wir an die heilende Kraft der Berührung. Unsere professionellen Therapeuten kombinieren traditionelle Massagetechniken mit modernen Wellness-Praktiken, um Ihnen ein wahrhaft transformatives Erlebnis zu bieten. Jede Sitzung wird auf Ihre individuellen Bedürfnisse zugeschnitten und gewährleistet optimale Entspannung und therapeutische Vorteile.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-secondary/20 rounded-xl p-6 border border-secondary/30 text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Natürlicher Ansatz</h3>
              <p className="text-text-secondary">Wir verwenden nur natürliche, hochwertige Öle und Produkte für alle unsere Behandlungen</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-secondary/20 rounded-xl p-6 border border-secondary/30 text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-primary" size={32} />
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Flexible Öffnungszeiten</h3>
              <p className="text-text-secondary">Mo-Fr: 09:00 - 18:00 Uhr, Sa: Nach Vereinbarung</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-secondary/20 rounded-xl p-6 border border-secondary/30 text-center"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-primary" size={32} />
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Zentrale Lage</h3>
              <p className="text-text-secondary">Kölnerstr. 296, 51645 Gummersbach</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Was unsere Kunden sagen</h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Hören Sie von unseren zufriedenen Kunden über ihre transformativen Erfahrungen
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20 relative"
            >
              <Quote className="text-accent/20 absolute top-6 right-6" size={48} />
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-accent fill-accent" size={20} />
                ))}
              </div>
              <p className="text-text-secondary mb-6 italic leading-relaxed">
                "Die Hot Stone Massage war absolut unglaublich! Ich fühlte, wie all mein Stress dahinschmolz. Die Therapeutin war professionell und die Atmosphäre so friedlich."
              </p>
              <div>
                <p className="font-bold text-primary">Sarah M.</p>
                <p className="text-sm text-text-muted">Stammkundin</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20 relative"
            >
              <Quote className="text-accent/20 absolute top-6 right-6" size={48} />
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-accent fill-accent" size={20} />
                ))}
              </div>
              <p className="text-text-secondary mb-6 italic leading-relaxed">
                "Ich komme seit Monaten zu Elysana. Die ayurvedische Massage ist mein Favorit! Ich gehe immer erfrischt und verjüngt nach Hause."
              </p>
              <div>
                <p className="font-bold text-primary">Michael K.</p>
                <p className="text-sm text-text-muted">Monatlicher Besucher</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20 relative"
            >
              <Quote className="text-accent/20 absolute top-6 right-6" size={48} />
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-accent fill-accent" size={20} />
                ))}
              </div>
              <p className="text-text-secondary mb-6 italic leading-relaxed">
                "Das beste Spa-Erlebnis in der Stadt! Die Thai-Massage war perfekt für meine Rückenschmerzen. Ich empfehle diesen Ort jedem!"
              </p>
              <div>
                <p className="font-bold text-primary">Anna L.</p>
                <p className="text-sm text-text-muted">Erstkunde</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-secondary/20">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Häufig gestellte Fragen
            </h2>
            <p className="text-lg text-text-secondary">
              Finden Sie Antworten auf häufige Fragen zu unseren Leistungen
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <Accordion type="single" collapsible className="w-full space-y-4">
              <AccordionItem 
                value="item-0"
                className="bg-secondary/10 rounded-xl border border-secondary/20 px-6"
                data-testid="faq-item-0"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary pr-4">Welche Arten von Massagen bieten Sie an?</span>
                </AccordionTrigger>
                <AccordionContent className="text-text-secondary pb-6 leading-relaxed">
                  Wir bieten eine Vielzahl von Massagen an, darunter Entspannungsmassagen, therapeutische Massagen und spezielle Wellnessbehandlungen.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem 
                value="item-1"
                className="bg-secondary/10 rounded-xl border border-secondary/20 px-6"
                data-testid="faq-item-1"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary pr-4">Wie lange dauern die Behandlungen?</span>
                </AccordionTrigger>
                <AccordionContent className="text-text-secondary pb-6 leading-relaxed">
                  Die Behandlungen dauern in der Regel zwischen 30 und 90 Minuten, je nach gewählter Massage.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem 
                value="item-2"
                className="bg-secondary/10 rounded-xl border border-secondary/20 px-6"
                data-testid="faq-item-2"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary pr-4">Sind die Massagen für jeden geeignet?</span>
                </AccordionTrigger>
                <AccordionContent className="text-text-secondary pb-6 leading-relaxed">
                  Ja, unsere Massagen sind für Erwachsene jeden Alters geeignet, jedoch empfehlen wir eine vorherige Rücksprache mit uns, wenn Sie gesundheitliche Bedenken haben.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem 
                value="item-3"
                className="bg-secondary/10 rounded-xl border border-secondary/20 px-6"
                data-testid="faq-item-3"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary pr-4">Wie kann ich einen Termin buchen?</span>
                </AccordionTrigger>
                <AccordionContent className="text-text-secondary pb-6 leading-relaxed">
                  Sie können einen Termin telefonisch oder über unser Kontaktformular auf der Website buchen.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem 
                value="item-4"
                className="bg-secondary/10 rounded-xl border border-secondary/20 px-6"
                data-testid="faq-item-4"
              >
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary pr-4">Was sind Ihre Öffnungszeiten?</span>
                </AccordionTrigger>
                <AccordionContent className="text-text-secondary pb-6 leading-relaxed">
                  Unsere Öffnungszeiten sind Montag bis Freitag von 09:00 bis 18:00 Uhr und Samstag nach Vereinbarung.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Kontaktieren Sie uns</h2>
            <p className="text-lg text-text-secondary mb-12">
              Haben Sie Fragen? Wir helfen Ihnen gerne, die perfekte Behandlung zu finden
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-white/50 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <Phone className="text-primary mx-auto mb-4" size={32} />
                <h3 className="text-xl font-bold text-primary mb-2">Rufen Sie uns an</h3>
                <p className="text-text-secondary">Weitere Informationen auf unserer Website</p>
              </div>
              
              <div className="bg-white/50 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <Mail className="text-primary mx-auto mb-4" size={32} />
                <h3 className="text-xl font-bold text-primary mb-2">E-Mail</h3>
                <p className="text-text-secondary">info@elysana-massage.de</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1716893933701-73d59789bba7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxzcGElMjBtYXNzYWdlJTIwcmVsYXhpbmclMjBhdG1vc3BoZXJlfGVufDB8fHx8MTc2NDY4MDI1NXww&ixlib=rb-4.1.0&q=85"
            alt="Spa Hintergrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Bereit zum Entspannen?
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-primary-foreground/90 max-w-2xl mx-auto">
              Buchen Sie heute Ihren Termin und erleben Sie ultimative Entspannung und Wellness-Therapie
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/all-services')}
                className="bg-accent text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-accent/90 transition-all shadow-xl"
                data-testid="cta-book-now-btn"
              >
                Termin buchen
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/contact')}
                className="bg-transparent border-2 border-primary-foreground text-primary-foreground px-10 py-6 rounded-full text-lg font-bold hover:bg-primary-foreground/10 transition-all"
                data-testid="cta-contact-btn"
              >
                Kontaktieren Sie uns
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/up1s5d57_Screenshot_2025-12-10_at_01.58.41-removebg-preview.png"
              alt="Elysana Logo"
              className="h-10 w-auto brightness-0 invert"
            />
          </div>
          <p className="text-primary-foreground/80 mb-2">Erleben Sie Wellness, umarmen Sie Ruhe</p>
          <p className="text-sm text-primary-foreground/60 mb-4">Kölnerstr. 296, 51645 Gummersbach</p>
          <p className="text-sm text-primary-foreground/60">© 2025 Elysana Massage. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;